import { useParams, Link } from 'react-router-dom';
import { articles, articleList } from '@/mocks/article';
import Breadcrumb from '@/components/base/Breadcrumb';
import PageSEO from '@/components/base/PageSEO';
import NotFound from '@/pages/NotFound';

export default function ArticleDetail() {
  const { id } = useParams<{ id: string }>();
  const article = id ? articles[id] : null;

  if (!article) {
    return <NotFound />;
  }

  const relatedArticles = articleList
    .filter((a) => a.id !== article.id && a.category === article.category)
    .slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.summary,
    datePublished: article.date,
    author: {
      '@type': 'Organization',
      name: '17c',
    },
    publisher: {
      '@type': 'Organization',
      name: '17c',
    },
    image: article.image,
    articleSection: article.category,
  };

  return (
    <PageSEO
      title={`${article.title} - 17c文章详情 | 17c官网`}
      description={article.summary}
      keywords={`17c,${article.tags.join(',')},17c教程`}
      canonicalPath={`/article/${article.id}`}
      schema={articleSchema}
    >
      <article className="pt-24 pb-16 md:pt-28 md:pb-24 px-4 md:px-6 bg-background-50">
        <div className="max-w-4xl mx-auto fade-in-up">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: '文章中心', path: '/article' },
              { label: article.title },
            ]}
          />

          <div className="mb-8">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span className="px-3 py-1 text-xs rounded-full bg-secondary-100 text-secondary-700 font-medium">
                {article.category}
              </span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 text-xs rounded-full bg-accent-100 text-accent-700"
                >
                  {tag}
                </span>
              ))}
            </div>

            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4 leading-tight">
              {article.title}
            </h1>

            <div className="flex items-center gap-4 text-sm text-foreground-500 mb-6">
              <span>{article.date}</span>
              <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
              <span>{article.readTime} 阅读</span>
            </div>
          </div>

          <div className="mb-10 rounded-lg overflow-hidden">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-auto object-cover"
            />
          </div>

          <div
            className="prose-custom text-foreground-800 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {relatedArticles.length > 0 && (
            <div className="mt-16 pt-10 border-t border-background-200/60">
              <h3 className="text-lg font-semibold text-foreground-950 mb-6">
                相关文章
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedArticles.map((rel) => (
                  <Link
                    key={rel.id}
                    to={`/article/${rel.id}`}
                    className="group block rounded-lg bg-background-100 border border-background-200/60 overflow-hidden hover:border-primary-300 transition-all duration-300 cursor-pointer"
                  >
                    <div className="relative h-40 overflow-hidden">
                      <img
                        src={rel.image}
                        alt={rel.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <h4 className="text-sm font-semibold text-foreground-950 mb-1 group-hover:text-primary-600 transition-colors duration-200 line-clamp-2">
                        {rel.title}
                      </h4>
                      <span className="text-xs text-foreground-500">{rel.date}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </article>
    </PageSEO>
  );
}